import { useState, useEffect, useRef } from "react";
import { spreadTickerPairs } from "@/mocks/homepageData";

function getDecimalPlaces(pair: string) {
  if (
    pair.includes("XAU") ||
    pair.includes("XAG") ||
    pair.includes("BTC") ||
    pair.includes("ETH")
  ) {
    return 2;
  }
  return 5;
}

export default function SpreadTicker() {
  const trackRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef(spreadTickerPairs);
  const rafRef = useRef<number>(0);
  const positionRef = useRef(0);
  const [items] = useState(() => [...spreadTickerPairs, ...spreadTickerPairs]);

  // RAF animation loop — no CSS loop boundary jank
  useEffect(() => {
    const animate = () => {
      const track = trackRef.current;
      if (!track) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      const halfWidth = track.scrollWidth / 2;
      positionRef.current -= 0.6;

      // Seamless wrap: when first copy fully scrolled out, jump to identical position
      if (positionRef.current <= -halfWidth) {
        positionRef.current += halfWidth;
      }

      track.style.transform = `translate3d(${positionRef.current}px, 0, 0)`;
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Data updates via direct DOM manipulation — no React re-render
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const interval = setInterval(() => {
      const prev = dataRef.current;
      const next = prev.map((item) => {
        const jitter = (Math.random() - 0.5) * item.bid * 0.00016;
        const newBid = Math.max(0.00001, item.bid + jitter);
        const spreadNoise = (Math.random() - 0.5) * 0.2;
        const newSpread = Math.max(0.1, Math.round((item.spread + spreadNoise) * 10) / 10);
        const isCrypto = item.pair.includes("BTC") || item.pair.includes("ETH");
        const isMetal = item.pair.includes("XAU") || item.pair.includes("XAG");
        const pipSize = isCrypto ? 1 : isMetal ? 0.01 : 0.0001;
        const newAsk = newBid + newSpread * pipSize;
        const changeDir = Math.random() > 0.5 ? "+" : "-";
        const changeVal = (Math.random() * 0.3 + 0.01).toFixed(2);
        const newChange = `${changeDir}${changeVal}%`;
        return {
          ...item,
          bid: newBid,
          ask: newAsk,
          spread: newSpread,
          change: newChange,
        };
      });
      dataRef.current = next;

      const allItems = [...next, ...next];
      const children = track.children;
      const len = Math.min(allItems.length, children.length);

      for (let i = 0; i < len; i++) {
        const el = children[i] as HTMLElement;
        const item = allItems[i];
        const spans = el.getElementsByTagName("span");
        if (spans.length >= 6) {
          spans[1].textContent = item.bid.toFixed(getDecimalPlaces(item.pair));
          spans[3].textContent = item.ask.toFixed(getDecimalPlaces(item.pair));
          spans[5].textContent = `${item.spread.toFixed(1)} pips`;
          const changeSpan = spans[6];
          if (changeSpan) {
            changeSpan.textContent = item.change;
            changeSpan.className = `font-medium font-mono ${
              item.change.startsWith("+") ? "text-[#10B981]" : "text-[#EF4444]"
            }`;
          }
        }
      }
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full bg-[#0B0F19] border-b border-[#374151]/50 overflow-hidden">
      <div className="flex items-center h-9 md:h-10">
        <div className="flex-shrink-0 px-3 md:px-4 h-full flex items-center gap-2 bg-[#0B0F19] border-r border-[#374151]/50 z-10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
          <span className="text-[10px] md:text-xs font-bold text-[#10B981] uppercase tracking-wider whitespace-nowrap">
            Live Spreads
          </span>
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div
            ref={trackRef}
            className="flex items-center gap-6 md:gap-8 whitespace-nowrap"
          >
            {items.map((item, idx) => (
              <div
                key={`${item.pair}-${idx}`}
                className="flex items-center gap-2 md:gap-3 text-[11px] md:text-xs"
              >
                <span className="font-bold text-[#F8FAFC]">{item.pair}</span>
                <span className="text-[#64748B] font-mono">
                  {item.bid.toFixed(getDecimalPlaces(item.pair))}
                </span>
                <span className="text-[#374151]">/</span>
                <span className="text-[#64748B] font-mono">
                  {item.ask.toFixed(getDecimalPlaces(item.pair))}
                </span>
                <span className="text-[#374151]">|</span>
                <span className="font-semibold text-[#10B981] font-mono">
                  {item.spread.toFixed(1)} pips
                </span>
                <span
                  className={`font-medium font-mono ${
                    item.change.startsWith("+")
                      ? "text-[#10B981]"
                      : "text-[#EF4444]"
                  }`}
                >
                  {item.change}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}